import React, { useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CodOrder, CodPaymentTransaction } from "../../../types/codSalesRep";
import { formatCodAmount } from "../../../utils/codUtils";
import { CustomerReportRow, RepReportRow, ReportTotals } from "../../../utils/codReports";
import {
  ChartDatum,
  collectionsByMonth,
  MonthDatum,
  salesByMonth,
  topCustomers,
  topCustomersAcrossReps,
  topProducts,
  topReps,
} from "../../../utils/codReportCharts";
import { NJIM } from "./codStyles";

/*
 * Chart palette — validated (light surface #fff): categorical blue / orange / aqua pass the
 * lightness, chroma, CVD and normal-vision checks. Aqua is below 3:1 against white, so every
 * segment that uses it carries a visible label + value. Single-series charts use blue only.
 * Text never wears a series colour (values and labels stay in ink).
 */
export const SERIES = { blue: "#2a78d6", orange: "#eb6834", aqua: "#1baf7a", yellow: "#eda100" } as const;
const OTHER = "#b7b6b0";
const GRID = "#eceae4";

export const fmt = (value: number) => formatCodAmount(value);
/** Plain count / quantity format for non-money charts. */
export const fmtCount = (value: number) => value.toLocaleString("en-US");
/** Short axis/label form: 1.2M, 450K. Exact values are in tooltips and the table view. */
const compact = (value: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);

/* ── Card shell with a chart ⇄ table toggle ────────────────────────────────────── */

export interface TableSpec {
  columns: string[];
  rows: (string | number)[][];
}

export const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  table: TableSpec;
  empty?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, table, empty, children }) => {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: `1px solid ${NJIM.border}`,
        bgcolor: NJIM.white,
        p: { xs: 2, md: 2.5 },
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.ink }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{subtitle}</Typography>}
        </Box>
        {!empty && (
          <Button
            size="small"
            onClick={() => setShowTable((v) => !v)}
            sx={{ textTransform: "none", fontWeight: 600, color: NJIM.textSecondary, minWidth: 0, flexShrink: 0 }}
          >
            {showTable ? t("codReports.chartView") : t("codReports.tableView")}
          </Button>
        )}
      </Box>
      {empty ? (
        <Typography sx={{ fontSize: 13, color: NJIM.textMuted, py: 4, textAlign: "center" }}>
          {t("codReports.noChartData")}
        </Typography>
      ) : showTable ? (
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {table.columns.map((col, i) => (
                <Box
                  component="th"
                  key={col}
                  sx={{ textAlign: i === 0 ? "start" : "end", fontWeight: 700, color: NJIM.textSecondary, fontSize: 12, py: 0.75, borderBottom: `1px solid ${NJIM.border}` }}
                >
                  {col}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, i) => (
                  <Box
                    component="td"
                    key={i}
                    sx={{ textAlign: i === 0 ? "start" : "end", py: 0.6, borderBottom: `1px solid ${NJIM.border}`, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums" }}
                  >
                    {cell}
                  </Box>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

/* ── Horizontal bar chart (ranking: top customers / reps / products) ────────────── */

export const HBarChart: React.FC<{ data: ChartDatum[]; ariaLabel: string; format?: (value: number) => string }> = ({
  data,
  ariaLabel,
  format = fmt,
}) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <Box role="img" aria-label={ariaLabel} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {data.map((d, index) => {
        const isOther = d.key === "__other";
        return (
          <Tooltip
            key={d.key}
            placement="top"
            title={`${d.label}${d.sub && d.sub !== d.label ? ` (${d.sub})` : ""}: ${format(d.value)}`}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 38%) minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 1.25,
                py: 0.25,
                borderRadius: "6px",
                cursor: "default",
                "&:hover": { bgcolor: NJIM.muted },
              }}
            >
              <Typography
                sx={{ fontSize: 12.5, color: NJIM.textPrimary, fontWeight: index === 0 ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {d.label}
              </Typography>
              <Box sx={{ height: 14, display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    height: 14,
                    width: `${Math.max((d.value / max) * 100, 1.5)}%`,
                    bgcolor: isOther ? OTHER : SERIES.blue,
                    // Rounded data end only; anchored square on the baseline (RTL-aware).
                    borderStartEndRadius: "4px",
                    borderEndEndRadius: "4px",
                    transition: "width 400ms cubic-bezier(0.23, 1, 0.32, 1)",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {compact(d.value)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/* ── Column chart (one series over time) ─────────────────────────────────────── */

export const ColumnChart: React.FC<{ data: MonthDatum[]; ariaLabel: string; format?: (value: number) => string }> = ({
  data,
  ariaLabel,
  format = fmt,
}) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  const peakIndex = data.reduce((best, d, i) => (d.value > data[best].value ? i : best), 0);
  const H = 150;
  return (
    <Box role="img" aria-label={ariaLabel} sx={{ position: "relative", pt: 2.5 }}>
      {/* Recessive grid: three hairlines */}
      {[0, 0.5, 1].map((f) => (
        <Box key={f} sx={{ position: "absolute", left: 0, right: 0, top: 20 + H * (1 - f), borderTop: `1px solid ${GRID}` }} />
      ))}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: H, position: "relative" }}>
        {data.map((d, i) => {
          const showLabel = i === peakIndex || i === data.length - 1;
          return (
            <Tooltip key={d.key} placement="top" title={`${d.label}: ${format(d.value)}`}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  position: "relative",
                  "&:hover > .col": { filter: "brightness(1.1)" },
                }}
              >
                {showLabel && d.value > 0 && (
                  <Typography sx={{ position: "absolute", top: `calc(${100 - (d.value / max) * 100}% - 18px)`, fontSize: 10.5, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap" }}>
                    {compact(d.value)}
                  </Typography>
                )}
                <Box
                  className="col"
                  sx={{
                    width: "70%",
                    maxWidth: 34,
                    height: `${d.value > 0 ? Math.max((d.value / max) * 100, 1.5) : 0}%`,
                    bgcolor: SERIES.blue,
                    borderRadius: "4px 4px 0 0",
                    transition: "height 400ms cubic-bezier(0.23, 1, 0.32, 1)",
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: "2px", mt: 0.75 }}>
        {data.map((d, i) => (
          <Typography
            key={d.key}
            sx={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: 10.5, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", visibility: data.length > 8 && i % 2 === 1 ? "hidden" : "visible" }}
          >
            {d.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/* ── Part-to-whole: one stacked bar with a labelled legend ────────────────────── */

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export const StackedBar: React.FC<{ segments: Segment[]; ariaLabel: string; format?: (value: number) => string }> = ({
  segments,
  ariaLabel,
  format = fmt,
}) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  const visible = segments.filter((s) => s.value > 0);
  return (
    <Box role="img" aria-label={ariaLabel}>
      <Box sx={{ display: "flex", gap: "2px", height: 22, borderRadius: "6px", overflow: "hidden", bgcolor: NJIM.muted }}>
        {visible.map((s) => (
          <Tooltip key={s.key} title={`${s.label}: ${format(s.value)} (${((s.value / total) * 100).toFixed(0)}%)`}>
            <Box sx={{ width: `${(s.value / total) * 100}%`, bgcolor: s.color, minWidth: 3 }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5 }}>
        {segments.map((s) => (
          <Box key={s.key} sx={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto auto", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: s.color }} />
            <Typography sx={{ fontSize: 12.5, color: NJIM.textPrimary }}>{s.label}</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums" }}>
              {format(s.value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, fontVariantNumeric: "tabular-nums", minWidth: 36, textAlign: "end" }}>
              {((s.value / total) * 100).toFixed(0)}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ── Collected vs remaining per rep (stacked rows, shared scale) ──────────────── */

const RepCollectionRows: React.FC<{ reps: RepReportRow[]; ariaLabel: string }> = ({ reps, ariaLabel }) => {
  const { t } = useTranslation();
  const rows = reps
    .filter((rep) => rep.collected + rep.remaining > 0)
    .sort((a, b) => b.collected + b.remaining - (a.collected + a.remaining))
    .slice(0, 8);
  const max = Math.max(...rows.map((r) => r.collected + r.remaining), 1);
  return (
    <Box role="img" aria-label={ariaLabel}>
      <Box sx={{ display: "flex", gap: 2, mb: 1.25 }}>
        {[
          { label: t("codReports.collected"), color: SERIES.blue },
          { label: t("codReports.remaining"), color: SERIES.orange },
        ].map((l) => (
          <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: l.color }} />
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{l.label}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map((rep) => (
          <Tooltip
            key={rep.key}
            placement="top"
            title={`${rep.repName}: ${t("codReports.collected")} ${fmt(rep.collected)} · ${t("codReports.remaining")} ${fmt(rep.remaining)}`}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 34%) minmax(0, 1fr) auto", alignItems: "center", gap: 1.25, "&:hover": { bgcolor: NJIM.muted }, borderRadius: "6px", py: 0.25 }}>
              <Typography sx={{ fontSize: 12.5, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {rep.repName}
              </Typography>
              <Box sx={{ display: "flex", gap: "2px", height: 14, width: `${((rep.collected + rep.remaining) / max) * 100}%`, minWidth: 4 }}>
                {rep.collected > 0 && <Box sx={{ flex: rep.collected, bgcolor: SERIES.blue }} />}
                {rep.remaining > 0 && (
                  <Box sx={{ flex: rep.remaining, bgcolor: SERIES.orange, borderStartEndRadius: "4px", borderEndEndRadius: "4px" }} />
                )}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {compact(rep.collected)} / {compact(rep.collected + rep.remaining)}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

/* ── Sections ─────────────────────────────────────────────────────────────── */

const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
  gap: 2,
} as const;

const moneySplit = (t: (k: string) => string, totals: ReportTotals): Segment[] => [
  { key: "collected", label: t("codReports.collected"), value: totals.collected, color: SERIES.blue },
  { key: "remaining", label: t("codReports.remaining"), value: totals.remaining, color: SERIES.orange },
  { key: "notInvoiced", label: t("codReports.notInvoiced"), value: totals.notInvoiced, color: SERIES.aqua },
];

const monthTable = (t: (k: string) => string, data: MonthDatum[], valueLabel: string): TableSpec => ({
  columns: [t("codReports.month"), valueLabel],
  rows: data.map((d) => [d.label, fmt(d.value)]),
});

const rankTable = (nameLabel: string, valueLabel: string, data: ChartDatum[]): TableSpec => ({
  columns: [nameLabel, valueLabel],
  rows: data.map((d) => [d.sub && d.sub !== d.label ? `${d.label} (${d.sub})` : d.label, fmt(d.value)]),
});

export const RepChartsSection: React.FC<{
  rows: CustomerReportRow[];
  orders: CodOrder[];
  payments: CodPaymentTransaction[];
  totals: ReportTotals;
}> = ({ rows, orders, payments, totals }) => {
  const { t } = useTranslation();
  const other = t("codReports.other");
  const sales = salesByMonth(orders);
  const collections = collectionsByMonth(payments);
  const customers = topCustomers(rows, 8, other);
  const products = topProducts(orders, 8, other);
  const split = moneySplit(t, totals);

  return (
    <Box sx={gridSx}>
      <ChartCard title={t("codReports.topCustomers")} subtitle={t("codReports.byApprovedValue")} empty={customers.length === 0} table={rankTable(t("codReports.customer"), t("codReports.approvedValue"), customers)}>
        <HBarChart data={customers} ariaLabel={t("codReports.topCustomers")} />
      </ChartCard>
      <ChartCard title={t("codReports.moneyStatus")} subtitle={t("codReports.moneyStatusHint")} empty={split.every((s) => s.value <= 0)} table={{ columns: ["", t("codReports.amount")], rows: split.map((s) => [s.label, fmt(s.value)]) }}>
        <StackedBar segments={split} ariaLabel={t("codReports.moneyStatus")} />
      </ChartCard>
      <ChartCard title={t("codReports.salesByMonth")} subtitle={t("codReports.byApprovedValue")} empty={sales.length === 0} table={monthTable(t, sales, t("codReports.approvedValue"))}>
        <ColumnChart data={sales} ariaLabel={t("codReports.salesByMonth")} />
      </ChartCard>
      <ChartCard title={t("codReports.collectionsByMonth")} empty={collections.length === 0} table={monthTable(t, collections, t("codReports.collected"))}>
        <ColumnChart data={collections} ariaLabel={t("codReports.collectionsByMonth")} />
      </ChartCard>
      <ChartCard title={t("codReports.topProducts")} subtitle={t("codReports.byApprovedValue")} empty={products.length === 0} table={rankTable(t("codReports.productName"), t("codReports.approvedValue"), products)}>
        <HBarChart data={products} ariaLabel={t("codReports.topProducts")} />
      </ChartCard>
    </Box>
  );
};

export const ManagerChartsSection: React.FC<{
  reps: RepReportRow[];
  orders: CodOrder[];
  payments: CodPaymentTransaction[];
  totals: ReportTotals;
}> = ({ reps, orders, payments, totals }) => {
  const { t } = useTranslation();
  const other = t("codReports.other");
  const repRanking = topReps(reps, 8, other);
  const customers = topCustomersAcrossReps(reps, 8, other);
  const sales = salesByMonth(orders);
  const collections = collectionsByMonth(payments);
  const split = moneySplit(t, totals);
  const outcomes: Segment[] = [
    { key: "approved", label: t("codSalesRep.approved"), value: totals.approved, color: SERIES.blue },
    { key: "pending", label: t("codSalesRep.pendingReview"), value: totals.pending, color: SERIES.orange },
    { key: "rejected", label: t("codSalesRep.rejected"), value: totals.rejected, color: SERIES.aqua },
  ];

  return (
    <Box sx={gridSx}>
      <ChartCard title={t("codReports.topReps")} subtitle={t("codReports.byApprovedValue")} empty={repRanking.length === 0} table={rankTable(t("codReports.salesRep"), t("codReports.approvedValue"), repRanking)}>
        <HBarChart data={repRanking} ariaLabel={t("codReports.topReps")} />
      </ChartCard>
      <ChartCard
        title={t("codReports.repCollection")}
        subtitle={t("codReports.repCollectionHint")}
        empty={reps.every((rep) => rep.collected + rep.remaining <= 0)}
        table={{
          columns: [t("codReports.salesRep"), t("codReports.collected"), t("codReports.remaining")],
          rows: reps.filter((r) => r.collected + r.remaining > 0).map((r) => [r.repName, fmt(r.collected), fmt(r.remaining)]),
        }}
      >
        <RepCollectionRows reps={reps} ariaLabel={t("codReports.repCollection")} />
      </ChartCard>
      <ChartCard title={t("codReports.salesByMonth")} subtitle={t("codReports.byApprovedValue")} empty={sales.length === 0} table={monthTable(t, sales, t("codReports.approvedValue"))}>
        <ColumnChart data={sales} ariaLabel={t("codReports.salesByMonth")} />
      </ChartCard>
      <ChartCard title={t("codReports.collectionsByMonth")} empty={collections.length === 0} table={monthTable(t, collections, t("codReports.collected"))}>
        <ColumnChart data={collections} ariaLabel={t("codReports.collectionsByMonth")} />
      </ChartCard>
      <ChartCard title={t("codReports.topCustomers")} subtitle={t("codReports.byApprovedValue")} empty={customers.length === 0} table={rankTable(t("codReports.customer"), t("codReports.approvedValue"), customers)}>
        <HBarChart data={customers} ariaLabel={t("codReports.topCustomers")} />
      </ChartCard>
      <ChartCard title={t("codReports.moneyStatus")} subtitle={t("codReports.moneyStatusHint")} empty={split.every((s) => s.value <= 0)} table={{ columns: ["", t("codReports.amount")], rows: split.map((s) => [s.label, fmt(s.value)]) }}>
        <StackedBar segments={split} ariaLabel={t("codReports.moneyStatus")} />
      </ChartCard>
      <ChartCard title={t("codReports.orderOutcomes")} empty={outcomes.every((s) => s.value <= 0)} table={{ columns: ["", t("codReports.orders")], rows: outcomes.map((s) => [s.label, s.value]) }}>
        <StackedBar
          segments={outcomes}
          ariaLabel={t("codReports.orderOutcomes")}
          format={(value) => value.toLocaleString("en-US")}
        />
      </ChartCard>
    </Box>
  );
};
